<script setup lang="ts">
import { onMounted, ref, computed } from 'vue'
import { useRoute } from 'vue-router'

import { VModal, VBadge, VButton, VLoading } from '@/modules/shared/components'
import DescriptionList from '@/modules/shared/components/DescriptionList.vue'

import { useInvoiceStore } from '../stores/invoice-store'
import { useAuthStore } from '@/modules/auth/stores/auth-store'
import { useModal } from '@/modules/shared/composables/use-modal'
import { useSkeleton } from '@/modules/core/composables/useSkeleton'
import { useExtendedI18n } from '@/i18n'

import { format } from '@/modules/shared/utils/v-table'
import { investorPath } from '../utils/investor'

import InvoiceBadge from './InvoiceBadge.vue'
import { Invoice } from '../types'
import { createQueryString } from '@/modules/shared/utils/routing'
import CallPaymentForm from './entities/distributions/CallPaymentForm.vue'

const route = useRoute()
const { n } = useExtendedI18n()

const props = defineProps<{
  invoiceId?: string
  status?: string
  callback?: any
  onOpenDetails?: any
  isOpenByDefault?: boolean
  enable_links?: boolean
}>()

const { slug } = route.params as { slug: string }
const store = useInvoiceStore()
const authStore = useAuthStore()

const isAdmin = computed(() => authStore.is_site_or_group_admin)

const wiringInstructionsSection = ref<HTMLElement | null>(null)
const focusedOn = ref<string | null>(null)

onMounted(async () => {
  if (props.isOpenByDefault) {
    await fetchAndOpenModal()
  }
  if (route.query.call_section === 'wiring-instructions') {
    wiringInstructionsSection.value?.scrollIntoView({ behavior: 'smooth' })
    focusedOn.value = 'wiring-instructions'
  }

  setTimeout(() => {
    focusedOn.value = null
  }, 1000)
})

const invoice = ref<Invoice>({} as Invoice)
const invoice_status = computed(() => props.status || invoice.value?.status || null)

const updateURLQuery = () => {
  const query = {
    ...route.query,
    invoice_id: props.invoiceId,
  }
  const queryString = createQueryString(query)
  window.history.replaceState(null, null, `?${queryString}`)
}

const modal = useModal()
const { skeleton, hideSkeleton, showSkeleton } = useSkeleton()
hideSkeleton()

const callback = async () => {
  if (props.callback) await props.callback()
}

function getDescriptionListItems(invoice: any) {
  const nValue = (val: any) => n(val, 'currency') || '--'

  const common = [
    { label: 'Total Due', value: nValue(invoice?.total_amount), key: 'total_amount' },
    { label: 'Entity', value: invoice.entity?.name, key: 'entity' },
    { label: 'Investor', value: invoice?.investor?.name, key: 'investor' },
  ]

  if (props.invoiceId) {
    const extra = [
      { label: 'Due on', value: invoice?.date, key: 'date' },
      { label: 'Status', value: invoice?.marked_as_paid || '--', key: 'marked_as_paid' },
      { label: 'Paid Date', value: format(invoice?.paid_at, 'date') || '--', key: 'paid_at' },
    ]
    return [...common.slice(0, 4), ...extra, ...common.slice(4)]
  }

  return common
}

///////////////////////////////////////////////////////////////////////////////
// Actions
///////////////////////////////////////////////////////////////////////////////

const fetchAndOpenModal = async () => {
  if (!props.invoiceId) return
  showSkeleton()
  modal.open()
  updateURLQuery()
  invoice.value = await store.fetchInvoice(props.invoiceId, { slug })
  if (props.onOpenDetails) await props.onOpenDetails()
  hideSkeleton()
}
</script>

<template>
  <template v-if="$slots.default">
    <slot :fetchAndOpenModal="fetchAndOpenModal"></slot>
  </template>
  <div v-else class="flex justify-center">
    <button v-if="invoiceId" @click="fetchAndOpenModal" class="relative cursor-pointer" :disabled="skeleton">
      <div class="relative cursor-pointer" :class="skeleton ? 'opacity-50' : ''">
        <template v-if="$slots.label">
          <slot name="label"></slot>
        </template>
        <template v-else>
          <InvoiceBadge :status="invoice_status" />
        </template>
      </div>

      <template v-if="skeleton">
        <div class="absolute inset-0 flex items-center justify-center">
          <VLoading />
        </div>
      </template>
    </button>
  </div>

  <VModal :modalStore="modal" :has_footer="false">
    <template #main>
      <div class="flex flex-col space-y-6">
        <div>
          <div class="rounded-lg bg-gradient-to-r from-[#3982AF] to-[#408F92] px-4 py-6 text-center text-white">
            <p class="text-sm font-medium opacity-80">Capital Call</p>
            <div class="mt-1 text-3xl font-semibold tracking-tight">
              <template v-if="skeleton">--</template>
              <template v-else>{{ n(invoice?.total_amount, 'currency') }}</template>
            </div>
          </div>
        </div>
        <div>
          <DescriptionList :items="getDescriptionListItems(invoice)" :loading="skeleton" title="Invoice Details">
            <template #marked_as_paid>
              <VBadge color="V_PAID" :dot="false" v-if="invoice.marked_as_paid"> Paid </VBadge>
              <VBadge color="V_UNPAID" :dot="false" v-else>Unpaid</VBadge>
            </template>

            <template #entity>
              <RouterLink
                v-if="enable_links && invoice.entity"
                class="hyperlink"
                :to="investorPath(invoice.entity?._cid)"
              >
                {{ invoice.entity?.name }}
              </RouterLink>
              <span v-else>{{ invoice.entity?.name }}</span>
            </template>

            <template #investor>
              <RouterLink v-if="enable_links" class="hyperlink" :to="investorPath(invoice.investor?._cid)">
                {{ invoice.investor?.name }}
              </RouterLink>
              <span v-else>{{ invoice.investor?.name }}</span>
            </template>
          </DescriptionList>
        </div>
        <CallPaymentForm v-if="isAdmin" :invoice="invoice" :refetch="callback" />

        <div>
          <DescriptionList
            v-if="invoice.wiring_instructions"
            :items="[{ label: invoice.wiring_instructions, value: '', key: 'wiring_instructions' }]"
            :loading="skeleton"
            title="Wiring Instructions"
          >
          </DescriptionList>
        </div>

        <div>
          <DescriptionList
            v-if="invoice.notes"
            :items="[{ label: invoice.notes, value: '', key: 'notes' }]"
            :loading="skeleton"
            title="Notes"
          >
          </DescriptionList>
        </div>
      </div>
    </template>
  </VModal>
</template>
